/**
 * Canonical chart data types for poker hand visualization
 *
 * Goals:
 * - Compact representation (2D arrays)
 * - Support single and multi-segment hands
 * - Support both continuous values and categorical data
 * - Minimal metadata overhead
 * - Easy to write by hand or generate programmatically
 */

import type { ColorSchemeKey } from '@/lib/colorSchemes';

/**
 * Basic hand type classification
 */
export type HandType = 'pair' | 'suited' | 'offsuit';

/**
 * More specific hand categorization
 */
export type HandSubtype =
  | 'premium-pair' // AA, KK, QQ
  | 'high-pair' // JJ, TT
  | 'mid-pair' // 99, 88, 77
  | 'low-pair' // 66, 55, 44, 33, 22
  | 'broadway' // AK, AQ, AJ, AT, KQ, KJ, KT, QJ, QT, JT (suited or offsuit)
  | 'suited-connector' // Consecutive ranks, suited (e.g., 87s, 76s)
  | 'suited-one-gapper' // One gap between ranks, suited (e.g., 86s, 75s)
  | 'suited-two-gapper' // Two gaps between ranks, suited (e.g., 85s, 74s)
  | 'suited-ace' // Ax suited (non-broadway)
  | 'suited-other' // Other suited hands
  | 'offsuit-connector' // Consecutive ranks, offsuit
  | 'offsuit-one-gapper' // One gap between ranks, offsuit
  | 'offsuit-ace' // Ax offsuit (non-broadway)
  | 'offsuit-other'; // Other offsuit hands

/**
 * Card ranks in order (A high to 2 low).
 */
export const RANKS = ['A', 'K', 'Q', 'J', 'T', '9', '8', '7', '6', '5', '4', '3', '2'] as const;
export type Rank = (typeof RANKS)[number];

/**
 * Represents a single segment of a hand's value.
 * For simple cases, a hand has one segment (e.g., equity = 0.85).
 * For mixed strategies, a hand can have multiple segments (e.g., call 60%, raise 40%).
 */
export interface HandSegment {
  value: number; // 0-1 normalized value (will determine height)
  label?: string; // e.g., "call", "raise", "fold"
  color?: string; // optional color override
}

/**
 * Segment value - can be a single number or labeled segment
 */
export type SegmentValue = number | { value: number; label?: string };

/**
 * Cell in the 13x13 grid
 * - null = not in range / undefined
 * - number = single segment with that value
 * - array = multiple segments (for mixed strategies)
 * - string = category label (for categorical charts)
 */
export type HandCell = null | number | string | SegmentValue[];

/**
 * Chart display defaults that can be specified in chart data
 * These override the global defaults when the chart is loaded
 *
 * colorScheme can be either:
 * - A ColorSchemeKey (e.g., "CATEGORICAL_5") when defined in JSON
 * - A resolved ColorScheme object when defined inline in code
 */
export interface ChartDefaults {
  /** Maximum bar height (0.5-4.0, default 2.0) */
  maxHeight?: number;
  /** Show the legend panel */
  showLegend?: boolean;
  /** Show values on bars */
  showValues?: boolean;
  /** Use logarithmic scale */
  useLogScale?: boolean;
  /** Segment layout: 'bookcase' for side-by-side, 'stacked' for stacked */
  segmentLayout?: 'bookcase' | 'stacked';
  /** Gap between segments (0-0.1) */
  segmentGap?: number;
  /** Color scheme key or resolved ColorScheme object */
  colorScheme?: ColorSchemeKey;
}

/**
 * Compact chart data format using 13x13 grid
 *
 * Grid layout:
 * - Row 0 = A, Row 12 = 2
 * - Col 0 = A, Col 12 = 2
 * - Diagonal = pairs (AA at [0,0], 22 at [12,12])
 * - Upper triangle = suited
 * - Lower triangle = offsuit
 */
export interface ChartData {
  /** Schema version (currently 1) */
  version: number;
  name: string;
  description?: string;

  /**
   * 13x13 grid of hand values
   * Each row represents a rank (A-2), each column represents a rank (A-2)
   */
  hands: HandCell[][];

  /**
   * Optional unit label (e.g., "%", "BB", "chips")
   */
  unit?: string;

  /**
   * Optional value range for normalization
   * If omitted and values are 0-1, assumes already normalized
   */
  valueRange?: { min: number; max: number };

  /**
   * Optional category mapping for enumerated values
   * When present, cell values should be category labels (strings)
   */
  categoryMapping?: {
    categories: string[];
    direction?: 'ascending' | 'descending';
  };

  /**
   * Optional display defaults for this chart
   * These are applied when the chart is loaded, overriding global settings
   */
  defaults?: ChartDefaults;

  /**
   * Optional graduation marks (horizontal lines at specific values)
   * Like measurement lines on a graduated cylinder or beaker
   * Values are in the same units as the hand values (raw, not normalized)
   */
  graduations?: number[];
}

/**
 * Represents a single hand with metadata (derived from grid + hand metadata)
 * This is generated on-the-fly for rendering purposes
 */
export interface HandData {
  /** Hand notation, e.g., "AA", "AKs", "72o" */
  hand: string;
  /** Row index (0-12, where 0 = A, 12 = 2) */
  row: number;
  /** Column index (0-12, where 0 = A, 12 = 2) */
  col: number;
  /** Whether this is a pair (diagonal), suited (above diagonal), or offsuit (below diagonal) */
  type: HandType;
  /** More specific categorization of the hand */
  subtype: HandSubtype;
  /** One or more value segments for this hand */
  segments: HandSegment[];
}
